import { Tooltip, TooltipContent, TooltipTrigger } from "@elit-hair/elit-ui";
import { AnimatePresence, motion } from "framer-motion";
import { t } from "i18next";
import { useLayoutEffect, useState } from "react";
import { TbPlayerStop, TbSend } from "react-icons/tb";
import { useTypewriter } from "react-simple-typewriter";

import { ChatInputProps } from "@/components/ai-chat/types/chat-input.types";
import {
    AI_INPUT_TYPEWRITER_DELAY_SPEED_MS,
    AI_INPUT_TYPEWRITER_DELETE_SPEED_MS,
    AI_INPUT_TYPEWRITER_PLACEHOLDERS,
    AI_INPUT_TYPEWRITER_TYPE_SPEED_MS,
    CHAT_INPUT_MAX_HEIGHT_PX,
    CHAT_INPUT_MIN_HEIGHT_PX,
    CHAT_INPUT_TYPEWRITER_MIN_HEIGHT_PX,
} from "@/constants/ai-chat";
import { cn } from "@/lib/utils";
import { scaleIn, withTransition } from "@/utils/animations";

const resizeTextarea = (textarea: HTMLTextAreaElement, minHeight: number) => {
    textarea.style.height = "auto";
    const nextHeight = Math.max(minHeight, Math.min(textarea.scrollHeight, CHAT_INPUT_MAX_HEIGHT_PX));
    textarea.style.height = `${nextHeight}px`;
};

function DarwinTypewriterPlaceholder() {
    const [typewriterText] = useTypewriter({
        words: [...AI_INPUT_TYPEWRITER_PLACEHOLDERS],
        loop: 0,
        typeSpeed: AI_INPUT_TYPEWRITER_TYPE_SPEED_MS,
        deleteSpeed: AI_INPUT_TYPEWRITER_DELETE_SPEED_MS,
        delaySpeed: AI_INPUT_TYPEWRITER_DELAY_SPEED_MS,
    });

    return (
        <span
            aria-hidden
            className="pointer-events-none absolute inset-0 z-0 overflow-hidden p-2 font-medium leading-6 text-gray-300 dark:text-white/30"
        >
            {typewriterText}|
        </span>
    );
}

export function ChatInput({
    inputRef,
    defaultValue,
    onValueChange,
    inputRevision,
    active = false,
    onSubmit,
    isLoading = false,
    placeholder = t("type_your_message"),
    className,
    isStreaming = false,
    onStop,
    useTypewriterPlaceholder = false,
}: ChatInputProps) {
    const [isEmpty, setIsEmpty] = useState(() => !defaultValue.trim());

    const isBusy = isLoading || isStreaming;
    const showStopButton = isBusy && Boolean(onStop);
    const showTypewriterPlaceholder = useTypewriterPlaceholder && isEmpty;
    const minHeight = showTypewriterPlaceholder ? CHAT_INPUT_TYPEWRITER_MIN_HEIGHT_PX : CHAT_INPUT_MIN_HEIGHT_PX;

    const canSubmit = () => Boolean(inputRef.current?.value.trim()) && !isLoading;

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const nextValue = e.target.value;
        onValueChange(nextValue);
        const nextEmpty = !nextValue.trim();
        setIsEmpty((current) => (current === nextEmpty ? current : nextEmpty));
        resizeTextarea(e.target, minHeight);
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (canSubmit()) {
            onSubmit(e);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (canSubmit()) {
                onSubmit();
            }
        }
    };

    useLayoutEffect(() => {
        const textarea = inputRef.current;
        if (!textarea) return;
        const nextEmpty = !textarea.value.trim();
        setIsEmpty((current) => (current === nextEmpty ? current : nextEmpty));
        resizeTextarea(textarea, minHeight);
    }, [inputRef, inputRevision, minHeight]);

    useLayoutEffect(() => {
        const textarea = inputRef.current;
        if (!textarea) return;
        if (!active) {
            if (document.activeElement === textarea) textarea.blur();
            return;
        }
        textarea.focus();
    }, [active, inputRef]);

    const handleTextareaClick = () => {
        inputRef.current?.focus();
    };

    return (
        <motion.form
            onSubmit={handleSubmit}
            className={cn("p-4 relative", className)}
            {...withTransition(scaleIn, { duration: 0.2 })}
        >
            <div className="flex items-end gap-4 relative z-10">
                <div className="flex-1 relative cursor-text pointer-events-auto" onClick={handleTextareaClick}>
                    {showTypewriterPlaceholder ? <DarwinTypewriterPlaceholder /> : null}
                    <textarea
                        ref={inputRef}
                        defaultValue={defaultValue}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        placeholder={showTypewriterPlaceholder ? "" : placeholder}
                        disabled={false}
                        className={cn(
                            "relative z-10 w-full resize-none border-none bg-transparent outline-none",
                            "placeholder:text-gray-300 dark:placeholder:text-white/30 text-gray-900 dark:text-white font-medium",
                            "max-h-[120px] p-2 leading-6",
                            "focus:ring-0 focus:outline-none",
                            "cursor-text pointer-events-auto",
                            isLoading && "opacity-50"
                        )}
                        style={{ minHeight }}
                        rows={showTypewriterPlaceholder ? 2 : 1}
                    />
                </div>

                <div className="flex items-center gap-2">
                    <AnimatePresence mode="wait" initial={false}>
                        {showStopButton ? (
                            <Tooltip key="stop">
                                <TooltipTrigger asChild>
                                    <motion.button
                                        type="button"
                                        onClick={onStop}
                                        {...withTransition(scaleIn, { duration: 0.2 })}
                                        className={cn(
                                            "p-3 rounded-full transition-all duration-300 relative overflow-hidden group",
                                            "bg-gradient-to-br from-red-500 via-orange-500 to-red-600",
                                            "text-white shadow-xl border-none",
                                            "hover:shadow-2xl hover:scale-105"
                                        )}
                                    >
                                        <TbPlayerStop className="w-5 h-5 relative z-10 drop-shadow-sm group-hover:scale-110 transition-transform duration-200" />
                                    </motion.button>
                                </TooltipTrigger>
                                <TooltipContent
                                    side="top"
                                    className="bg-black/80 backdrop-blur-sm border border-white/20 text-white"
                                >
                                    {t("stop_streaming")}
                                </TooltipContent>
                            </Tooltip>
                        ) : (
                            <Tooltip key="send">
                                <TooltipTrigger asChild>
                                    <motion.button
                                        type="submit"
                                        {...withTransition(scaleIn, { duration: 0.2 })}
                                        disabled={isEmpty || isBusy}
                                        className={cn(
                                            "p-3 rounded-full transition-all duration-300 relative overflow-hidden group",
                                            "bg-gradient-to-br from-blue-500 via-purple-600 to-pink-500",
                                            "text-white shadow-xl border-none",
                                            "hover:shadow-2xl hover:scale-105",
                                            "disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100",
                                            "disabled:shadow-lg"
                                        )}
                                    >
                                        <TbSend className="w-5 h-5 relative z-10 drop-shadow-sm group-hover:scale-110 transition-transform duration-200" />
                                    </motion.button>
                                </TooltipTrigger>
                                <TooltipContent
                                    side="top"
                                    className="bg-black/80 backdrop-blur-sm border border-white/20 text-white"
                                >
                                    {t("send_message")}
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </motion.form>
    );
}
